import { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Spin } from 'antd';
import Heading from 'LearningManager/components/Heading';
import styles from 'LearningManager/styles/CustomSetup.module.scss';
import CheckBoxCard from 'LearningManager/components/CheckBoxCard';
import { Icon, Input, Notification } from 'Components';
import MultiSelectDropdown from 'LearningManager/components/MultiSelectDropdown';
import { useStepper } from 'LearningManager/hooks/Stepper.context';
import useAssignmentTenantId from 'LearningManager/hooks/useAssignmentTenantId';
import useAudienceTenantId from 'LearningManager/hooks/useAudienceTenantId';
import { useStores } from 'Hooks/useStore';
import { Status } from 'app-types';
import { DirectoryUser } from 'Services/Api/Directory/Types';

const matchesSearch = (user: DirectoryUser, search: string) => {
	const q = search.trim().toLowerCase();
	return (
		!q ||
		user.firstName.toLowerCase().includes(q) ||
		user.lastName.toLowerCase().includes(q) ||
		user.email.toLowerCase().includes(q)
	);
};

const toSelectedUser = (user: DirectoryUser) => ({
	id: user.id,
	firstName: user.firstName,
	lastName: user.lastName,
	email: user.email
});

const Individual = observer(() => {
	const { assignUsersStore } = useStores();
	const {
		users,
		usersTotal,
		hasMoreUsers,
		properties,
		status
	} = assignUsersStore;
	const { tenantId, isTenantResolved } = useAudienceTenantId(
		useAssignmentTenantId()
	);

	const {
		draft: {
			customSetup: {
				selectedIndividualUsers,
				selectedIndividualPropertyIds
			}
		},
		toggleIndividualUserSelection,
		setIndividualUserSelection,
		setAssignUsersPropertyFilter
	} = useStepper();

	const [search, setSearch] = useState('');
	const loadMoreRef = useRef<HTMLDivElement>(null);

	// Users are reached through properties, so scoping the property list to the
	// tenant scopes the users with it.
	useEffect(() => {
		if (!isTenantResolved) return;
		if (
			!properties.length ||
			assignUsersStore.propertiesTenantId !== tenantId
		) {
			assignUsersStore.fetchProperties(tenantId);
		}
	}, [isTenantResolved, tenantId]);

	useEffect(() => {
		assignUsersStore
			.fetchUsersByProperty(selectedIndividualPropertyIds)
			.catch(() => undefined);
	}, [selectedIndividualPropertyIds]);

	const isSearching = Boolean(search.trim());

	useEffect(() => {
		if (isSearching && hasMoreUsers) {
			assignUsersStore.fetchAllUsers().catch(() => undefined);
		}
	}, [isSearching, hasMoreUsers]);

	const isLoading = status.fetchUsers === Status.Pending;
	const isLoadingMore = status.fetchMoreUsers === Status.Pending;
	const hasLoadMoreFailed = status.fetchMoreUsers === Status.Failure;
	const isSelectingAll = status.fetchAllUsers === Status.Pending;
	const canLoadMore =
		hasMoreUsers && !isLoading && !isLoadingMore && !hasLoadMoreFailed;

	useEffect(() => {
		const node = loadMoreRef.current;
		if (!node || !canLoadMore) return;
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					assignUsersStore.fetchNextUsersPage().catch(() => undefined);
				}
			},
			{ rootMargin: '200px' }
		);
		observer.observe(node);
		return () => observer.disconnect();
	}, [canLoadMore, users.length]);

	const propertyOptions = properties.map((p) => ({ id: p.id, label: p.name }));

	const filtered = isSearching
		? users.filter((u) => matchesSearch(u, search))
		: users;

	const selectedIds = useMemo(
		() => new Set(selectedIndividualUsers.map((u) => u.id)),
		[selectedIndividualUsers]
	);

	const resultCount = isSearching ? filtered.length : usersTotal;

	const handleCheckboxChange = (id: number | string, checked: boolean) => {
		const uuid = String(id);
		const user = users.find((u) => u.id === uuid);
		if (!user) return;
		if (checked !== selectedIds.has(uuid)) {
			toggleIndividualUserSelection(toSelectedUser(user));
		}
	};

	const isAllSelected =
		!hasMoreUsers &&
		filtered.length > 0 &&
		filtered.every((u) => selectedIds.has(u.id));

	const handleSelectAll = async () => {
		if (isSelectingAll) return;
		if (isAllSelected) {
			setIndividualUserSelection([]);
			return;
		}

		let pool = users;
		if (hasMoreUsers) {
			try {
				const allUsers = await assignUsersStore.fetchAllUsers();
				if (!allUsers) return;
				pool = allUsers;
			} catch {
				Notification.error({
					description: 'Could not load all users. Please try again.'
				});
				return;
			}
		}

		setIndividualUserSelection(
			pool.filter((u) => matchesSearch(u, search)).map(toSelectedUser)
		);
	};

	return (
		<div id="lms-ca-assign-users-individual">
			<div>
				<Heading />
				<div className={styles.filters}>
					<div
						id="lms-ca-au-individual-property-filter"
						className={styles.search}
					>
						<div className={styles.selectProp}>Select Property</div>
						<MultiSelectDropdown
							options={propertyOptions}
							placeholder="Select option"
							onChange={(ids) => {
								setAssignUsersPropertyFilter(
									'selectedIndividualPropertyIds',
									ids
								);
							}}
							defaultSelectedIds={selectedIndividualPropertyIds}
						/>
					</div>
					<Input.Search
						id="lms-ca-au-individual-search"
						className={styles.search}
						placeholder="Search by name or email"
						suffix={<Icon type="search" role="img" />}
						// Keeps a suffix rendered, so the input isn't remounted mid-typing.
						allowClear
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
				</div>
			</div>
			<div className={styles.header}>
				<Heading
					titleClassName={styles.title}
					title={`Select Individuals (${resultCount} Results)`}
				/>
				{resultCount > 1 && (
					<div
						id="lms-ca-au-individual-select-all"
						className={styles.selectAllBtn}
						onClick={handleSelectAll}
					>
						{isSelectingAll
							? 'Loading users…'
							: isAllSelected
							? 'Deselect all'
							: 'Select all'}
					</div>
				)}
			</div>
			<div id="lms-ca-au-individual-list" className={styles.container}>
				{isLoading && <div className={styles.loading}><Spin /></div>}
				{!isLoading && selectedIndividualPropertyIds.length === 0 && (
					<div className={styles.empty}>
						Select a property to see available users.
					</div>
				)}
				{filtered.map((user) => {
					const isChecked = selectedIds.has(user.id);
					return (
						<CheckBoxCard
							key={user.id}
							id={user.id}
							htmlId={`lms-ca-au-individual-${user.id}`}
							icon={
								<Icon
									type={
										isChecked
											? 'indiOptFilled'
											: 'individualOptIcon'
									}
								/>
							}
							title={`${user.firstName} ${user.lastName}`}
							desc={user.email}
							isChecked={isChecked}
							onChange={handleCheckboxChange}
						/>
					);
				})}
				{hasMoreUsers && !isLoading && (
					<div
						id="lms-ca-au-individual-load-more"
						ref={loadMoreRef}
						className={styles.loadMore}
					>
						{isLoadingMore && <Spin />}
						{hasLoadMoreFailed && (
							<>
								<span className={styles.empty}>
									Could not load more users.
								</span>
								<div
									id="lms-ca-au-individual-load-more-retry"
									className={styles.selectAllBtn}
									onClick={() =>
										assignUsersStore
											.fetchNextUsersPage()
											.catch(() => undefined)
									}
								>
									Try again
								</div>
							</>
						)}
					</div>
				)}
			</div>
		</div>
	);
});

export default Individual;
